import { Component, Input, OnChanges, OnDestroy, SimpleChanges } from '@angular/core';
import { EncSeries, Encoding } from '@mode-vegas/vegas';
import { trackByFactory } from '@mode/capra';
import { SelectedChartService } from '@mode/charts/contract';
import { EncodingHelpers, RenderDataModel } from '@mode/charts/util-vegas';
import { BinSizeTypes, ChartTypes, FlamingoTypes } from '@mode/shared/contract-common';
import { FLAMINGO_RESERVED_REFS } from '@mode/shared/util-js';
import { CompiledField } from '@mode/shared/util-switch';
import { Observable, Subject, combineLatest, of, takeUntil } from 'rxjs';
import * as MarksHelpers from '../marks-helpers';

@Component({
  selector: 'mode-marks-panel',
  templateUrl: './marks-panel.component.html',
  styleUrls: ['./marks-panel.component.scss'],
})
export class MarksPanelComponent implements OnChanges, OnDestroy {
  // TODO: Migrate this to ChartObservables type, currently type live in legacy and needs to be migrated.
  @Input({ required: true }) observables!: {
    token$: Observable<string>;
    chartType$: Observable<ChartTypes.ChartType>;
    colorPaletteToken$: Observable<string>;
    encoding$: Observable<Encoding>;
    format$: Observable<ChartTypes.ChartFormat>;
    fieldFormats$: Observable<ChartTypes.FieldFormatMap>;
    fieldColors$: Observable<ChartTypes.FieldColorMap>;
    facetSizes$: Observable<ChartTypes.FacetSizeMap>;
    isAutoOrManualRender$: Observable<boolean>;
    queryToken$: Observable<string>;
    queryRunToken$: Observable<string>;
    view$: Observable<FlamingoTypes.ModeView | undefined>;
    renderModel$: Observable<RenderDataModel>;
    binSizeToFieldMap$: Observable<BinSizeTypes.BinSizeToFieldMap>;
  };

  @Input({ required: true }) model!: FlamingoTypes.ModeModel;
  @Input({ required: true }) chartService!: SelectedChartService;
  @Input() isExploring = false;

  queryToken$: Observable<string> | undefined;
  queryRunToken$: Observable<string> | undefined;
  vizToken$: Observable<string> | undefined;
  view$: Observable<FlamingoTypes.ModeView | undefined> | undefined;
  encoding$: Observable<Encoding> | undefined;
  encoding: Encoding | undefined;
  format$: Observable<ChartTypes.ChartFormat> | undefined;
  fieldColors$: Observable<ChartTypes.FieldColorMap> | undefined;
  fieldFormats$: Observable<ChartTypes.FieldFormatMap> | undefined;
  colorPaletteToken$: Observable<string> | undefined;
  renderModel$: Observable<RenderDataModel> | undefined;
  isAutoOrManualRender$: Observable<boolean> | undefined;
  chartType$: Observable<ChartTypes.ChartType> | undefined;
  binSizeToFieldMap$: Observable<BinSizeTypes.BinSizeToFieldMap> | undefined;

  private destroyed$ = new Subject<void>();

  expandedSeries: string | null = null;
  readonly ALL_LAYERS = 'all_layers';
  panelState: Record<string, 'open' | 'closed'> = { [this.ALL_LAYERS]: 'open' };

  trackBySeries = trackByFactory<EncSeries>(({ field }) => field);

  layerInfoTooltip =
    `Use layers to add additional context to your visualization. ` +
    `"All Layers" will apply the same configuration to every layer.`;

  iconMarkMap = MarksHelpers.IconMarkMap;
  iconWarning = MarksHelpers.IconWarning;

  seriesDisplayNames: Record<string, string> = {};
  seriesIsValid: Record<string, boolean> = {};

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['observables']) {
      this.queryToken$ = this.observables.queryToken$;
      this.queryRunToken$ = this.observables.queryRunToken$;
      this.view$ = this.observables.view$;
      this.encoding$ = this.observables.encoding$;
      this.format$ = this.observables.format$;
      this.fieldColors$ = this.observables.fieldColors$;
      this.fieldFormats$ = this.observables.fieldFormats$;
      this.colorPaletteToken$ = this.observables.colorPaletteToken$;
      this.renderModel$ = this.observables.renderModel$;
      this.isAutoOrManualRender$ = this.observables.isAutoOrManualRender$;
      this.chartType$ = this.observables.chartType$;
      this.binSizeToFieldMap$ = this.observables.binSizeToFieldMap$;

      this.vizToken$ = this.isExploring ? of(FLAMINGO_RESERVED_REFS.Exploration) : this.observables.token$;

      this.encoding$.pipe(takeUntil(this.destroyed$)).subscribe((encoding) => {
        this.encoding = encoding;
      });

      combineLatest([this.encoding$, this.view$])
        .pipe(takeUntil(this.destroyed$))
        .subscribe(([encoding, view]) => {
          if (!view) {
            return;
          }
          const displayNameMap: Record<string, string> = {};
          encoding.marks.series.forEach((series) => {
            const encField = EncodingHelpers.getSeriesFieldById(encoding, series.field);
            if (encField) {
              displayNameMap[series.field] = CompiledField.fromEncField(view, encField).displayFormula;
            }
          });
          this.seriesDisplayNames = displayNameMap;
        });
    }
  }

  ngOnDestroy() {
    this.destroyed$.next();
    this.destroyed$.complete();
  }

  onSeriesValidChange($event: { seriesId: string; isValid: boolean }) {
    const { seriesId, isValid } = $event;
    this.seriesIsValid[seriesId] = isValid;
  }

  isPanelOpen(panelId: string) {
    return this.panelState[panelId] === 'open';
  }

  expandPanel(series: string | null) {
    this.expandedSeries = series;
    const key = series ?? this.ALL_LAYERS;
    this.panelState[key] = 'open';
  }

  closePanel(panelId: string) {
    this.panelState[panelId] = 'closed';
  }
}
